import { useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { UpdateFullOrganizationDetailsResponse } from "@requests/organization-details/update-full-organization-details";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { UpdateOrganizationUsersTableContainer } from "@containers/modals/update-organization-users-modal/update-organization-users-table-container/update-organization-users-table-container";
import styles from './update-organization-users-modal.less';

type UpdateOrganizationUsersModalProps = {
    organizationUsers: Array<AxnAdminUser>;
    onModalClose: () => void;
    cancelUpdateOrganizationDetailsWithCorporateLogin: () => void;
    performUpdateOrganizationDetailsWithCorporateLogin: (organizationUsers: Array<AxnAdminUser>) => Promise<UpdateFullOrganizationDetailsResponse>;
}

export const UpdateOrganizationUsersModal = (props: UpdateOrganizationUsersModalProps) => {
    const {
        organizationUsers,
        onModalClose,
        cancelUpdateOrganizationDetailsWithCorporateLogin,
        performUpdateOrganizationDetailsWithCorporateLogin
    } = props;

    const [requestInProgress, setRequestInProgress] = useState(false);
    const [organizationUsersState, setOrganizationUsersState] = useState(organizationUsers);

    const handleUpdateCredentials = async () => {
        setRequestInProgress(true);

        const result = await performUpdateOrganizationDetailsWithCorporateLogin(organizationUsersState);

        if (result && !result.failedStep) {
            onModalClose();
        } else if (result?.failedStep && result?.axnAdminUsersNotUpdated) {
            setOrganizationUsersState(result.axnAdminUsersNotUpdated);
            setRequestInProgress(false);
        } else {
            setRequestInProgress(false);
        }
    };

    const handleOrganizationUserChange = (organizationUser: AxnAdminUser) => {
        const updatedOrganizationUsers = organizationUsersState.map(user => {
            if (user.id === organizationUser.id) {
                return organizationUser;
            }

            return user;
        });

        setOrganizationUsersState(updatedOrganizationUsers);
    }

    const handleAbortUserCredentialChanges = () => {
        cancelUpdateOrganizationDetailsWithCorporateLogin();
        onModalClose();
    }

    const modalResources = resources.modals.updateOrganizationUsersModal;
    
    return (<DefaultModal
        automationId="update-organization-users-modal"
        ariaLabelledBy="update-organization-users-modal"
        onHide={onModalClose}
        backdrop={"static"}
        keyboard={false}
        isMaxWidth={true}
    >
        <Modal.Header id={"update-organization-users-modal-title"} closeButton>
            <Modal.Title>{modalResources.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className={styles.enableUserCredentialsUpdate}>
                <Alert className={styles.description} variant={"info"}>{modalResources.description}</Alert>
            </div>
            <UpdateOrganizationUsersTableContainer
                organizationUsers={organizationUsersState}
                initialOrganizationUsers={organizationUsers}
                handleOrganizationUserChange={handleOrganizationUserChange}
            />
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={handleAbortUserCredentialChanges}>{resources.common.buttons.cancel.label}</Button>
            <ButtonWithSpinner
                onClick={handleUpdateCredentials}
                label={resources.common.buttons.save.label}
                type={ButtonTypes.BUTTON}
                variant={"primary"}
                disabled={requestInProgress}
            />
        </Modal.Footer>
    </DefaultModal>);
}